import { ImagesSkusFiltersBlackSvg, ImagesSkusCloseSvg } from '@/assets/images_skus.assets';
import Image from '@/components/common/SpiritoryImage';
import { useEffect, useRef } from 'react';
import SkusMobileSearchShowMenuFilter from './SkusMobileSearchShowMenuFilter';
import SkusMobileSearchShowStatusFilter from './SkusMobileSearchShowStatusFilter';
import SkusMobileSearchShowPricesFilter from './SkusMobileSearchShowPricesFilter';
import SkusMobileSearchRefinementList from './SkusMobileSearchRefinementList';
import { useCurrentRefinements } from 'react-instantsearch';
import DefaultButton from '@/components/common/default/DefaultButton';
import { CgClose } from 'react-icons/cg';
import { SkusMobileSearchShowFiltersProps, FilterConfig } from '@/types/props/components/skus';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkusEnum } from '@/types/translations/SkusEnum';
import { CommonEnum } from '@/types/translations/CommonEnum';

export default function SkusMobileSearchShowFilters({
  setFiltersAreOpen,
  filtersAreOpen,
  filterConfig = {},
}: SkusMobileSearchShowFiltersProps) {
  const { translate } = useTypedTranslation<SkusEnum>('skus');
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const { items, refine } = useCurrentRefinements();

  // Default filter configuration - all filters enabled by default
  const defaultFilterConfig: FilterConfig = {
    status: true,
    category: true,
    brand: true,
    price: true,
    country: true,
    region: true,
    size: true,
    // Was `yearBottled`, which could not work — the field was dropped from the search document
    // as an unread duplicate of `createdTimestamp`, so the control rendered nothing. Repointed
    // at the `distilledYear` facet (BAC-168).
    year: true,
  };

  // This panel deliberately renders the same set as the desktop sidebar (`FilterSkus`), which is
  // why `FilterConfig` is shared between them. Three controls were removed to get there:
  //
  //   subCategory      the field is not in the search document and never was (§12.4), so it
  //                    always rendered an empty list
  //   volumeInPercent  desktop never had it
  //   productAge       desktop never had it, and 90% of the catalogue sits in a `-1` "no age
  //                    statement" bucket that the indexer passes through raw — so the control's
  //                    top option was a checkbox labelled `-1` covering 19,317 bottles
  //
  // Adding any of them back means adding it to BOTH panels, and fixing the `-1` sentinel in
  // `search-document.util.ts` first for `productAge`.

  // Merge user config with defaults
  const mergedFilterConfig = { ...defaultFilterConfig, ...filterConfig };

  const getFilterLabel = (filter: any, refinement: any) => {
    if (filter.label === 'hasBid') {
      return translateCommon(CommonEnum['has_bids']);
    }

    if (filter.label === 'hasAsk') {
      return translateCommon(CommonEnum['has_asks']);
    }

    if (filter.attribute === 'lowestAsk') {
      const parts = refinement.label.split(' ');
      return `${parts[0]} ${parts[1] / 100}`;
    }

    return refinement.label;
  };
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogTitleId = 'filter-dialog-title';

  // Handle closing the dialog when the user clicks outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const dialogDimensions = dialogRef.current?.getBoundingClientRect();
    if (
      dialogDimensions &&
      (e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom)
    ) {
      handleClose();
    }
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // Handle ESC key and close button
  const handleClose = () => {
    document.body.style.overflow = '';
    setFiltersAreOpen(false);
  };

  useEffect(() => {
    if (filtersAreOpen) {
      dialogRef.current?.showModal();
      document.body.style.overflow = 'hidden';
    } else {
      dialogRef.current?.close();
      document.body.style.overflow = '';
    }
  }, [filtersAreOpen]);

  return (
    <dialog
      ref={dialogRef}
      className="tw-backdrop:bg-black tw-backdrop:bg-opacity-50 tw-fixed tw-inset-0 tw-z-[99] tw-m-0 tw-h-full tw-max-h-screen tw-min-h-screen tw-w-full tw-max-w-none tw-overflow-y-scroll tw-bg-white tw-p-0"
      aria-labelledby={dialogTitleId}
      aria-modal="true"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="tw-flex tw-h-full tw-flex-col">
        <header className="tw-flex tw-flex-col tw-gap-3 tw-border-0.75 tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-custom-gray tw-px-8 tw-pb-3 tw-pt-12">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-w-full tw-items-center tw-gap-3">
              <Image
                src={ImagesSkusFiltersBlackSvg}
                width={24}
                height={14.4}
                alt=""
                aria-hidden="true"
              />
              <p
                id={dialogTitleId}
                className="tw-m-0 tw-font-plusJakartaSans tw-text-xl tw-font-semibold tw-text-textBlack"
              >
                {translate(SkusEnum['filters'])}
              </p>
            </div>
            <DefaultButton
              ref={closeButtonRef}
              onClick={handleClose}
              aria-label={translate(SkusEnum['close_filters'])}
              className="tw-rounded-full tw-p-2 hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
            >
              <CgClose aria-hidden="true" />
            </DefaultButton>
          </div>
          {items.length > 0 && (
            <section
              className="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2 tw-pt-2"
              aria-label={translate(SkusEnum['active_filters']) || 'Active filters'}
            >
              {items.map(filter =>
                filter.refinements.map(refinement => (
                  <DefaultButton
                    key={refinement.value}
                    className="tw-flex tw-items-center tw-gap-4 tw-rounded-lg tw-bg-customGray tw-px-4 tw-py-2 tw-transition-colors hover:tw-bg-gray-200"
                    onClick={() => refine(refinement)}
                  >
                    <span>{getFilterLabel(filter, refinement)}</span>{' '}
                    <Image
                      src={ImagesSkusCloseSvg}
                      width={10.5}
                      height={10.5}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className="tw-sr-only">
                      {translate(SkusEnum['remove_filterName_filter']).replace(
                        '{filterName}',
                        String(refinement.value)
                      )}
                    </span>
                  </DefaultButton>
                ))
              )}
            </section>
          )}
        </header>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-8">
          <section className="tw-pt-3">
            <nav aria-label={translate(SkusEnum['primary_filters']) || 'Primary filters'}>
              <ul className="tw-flex tw-w-full tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-custom-gray tw-p-0 tw-pb-4">
                {mergedFilterConfig.category && <SkusMobileSearchShowMenuFilter />}
                {mergedFilterConfig.status && <SkusMobileSearchShowStatusFilter />}
                {mergedFilterConfig.price && <SkusMobileSearchShowPricesFilter />}
              </ul>
            </nav>
            <nav aria-label={translate(SkusEnum['secondary_filters']) || 'Secondary filters'}>
              <ul className="tw-flex tw-w-full tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-custom-gray tw-p-0 tw-pb-4">
                {mergedFilterConfig.country && (
                  <SkusMobileSearchRefinementList
                    attributeName="brand.countryOfOrigin"
                    title={translate(SkusEnum['country'])}
                  />
                )}
                {mergedFilterConfig.brand && (
                  <SkusMobileSearchRefinementList
                    attributeName="brand.name"
                    title={translate(SkusEnum['brand'])}
                  />
                )}
                {mergedFilterConfig.region && (
                  <SkusMobileSearchRefinementList
                    attributeName="brand.region"
                    title={translate(SkusEnum['region'])}
                  />
                )}
                {mergedFilterConfig.size && (
                  <SkusMobileSearchRefinementList
                    attributeName="bottleSizeInMl"
                    title={translate(SkusEnum['bottle_size'])}
                  />
                )}
                {mergedFilterConfig.year && (
                  <SkusMobileSearchRefinementList
                    attributeName="distilledYear"
                    title={translate(SkusEnum['distilled_year'])}
                  />
                )}
              </ul>
            </nav>
          </section>
        </div>
      </div>
    </dialog>
  );
}
